import {
  ConnectionListResult,
  FeatureFlagsContext,
  GridSortProps,
  MaxCountsResultData,
  PaginatedQueryWrapper,
} from '@tableau/data-catalog-shared';
import * as React from 'react';

import { Messages } from '../../../gen-src/Localize';
import { DatabasesDisplayGridRowDataFragment } from '../../../gen-src/queries/DatabasesDisplayGridRowDataFragment';
import {
  VirtualConnectionTableUpstreamDatabasesFilteredByColumnQuery,
  VirtualConnectionTableUpstreamDatabasesFilteredByColumnQuery_virtualConnectionTables_columnsConnection_nodes_upstreamDatabasesConnection_nodes,
  VirtualConnectionTableUpstreamDatabasesFilteredByColumnQueryVariables,
} from '../../../gen-src/queries/VirtualConnectionTableUpstreamDatabasesFilteredByColumnQuery';
import {
  VirtualConnectionTableUpstreamDatabasesQuery,
  VirtualConnectionTableUpstreamDatabasesQuery_virtualConnectionTables_upstreamDatabasesConnection_nodes,
  VirtualConnectionTableUpstreamDatabasesQueryVariables,
} from '../../../gen-src/queries/VirtualConnectionTableUpstreamDatabasesQuery';
import { DatabaseSortOrder } from '../../../gen-src/queries/globalTypes';
import { VirtualConnectionTableUpstreamDatabasesFilteredByColumnQueryGql } from '../../queries/VirtualConnectionTableUpstreamDatabasesFilteredByColumnQueryGql';
import { VirtualConnectionTableUpstreamDatabasesQueryGql } from '../../queries/VirtualConnectionTableUpstreamDatabasesQueryGql';
import { getRowDataWithMaxCountsForDatabase } from '../database/functions/DatabaseRowDataFunctions';
import { DatabasesDisplayGridRowData } from '../database/types/DatabasesDisplayGridRowData';
import { DatabasesDisplayGrid } from '../sharedDisplayGrid/DatabasesDisplayGrid';
import { DatabasesValidSortFields } from '../sorting/sortProviders/DatabasesGridSortProvider';


type QueryVariables = VirtualConnectionTableUpstreamDatabasesQueryVariables;
type QueryResults = VirtualConnectionTableUpstreamDatabasesQuery;
type QueryResultListNode = VirtualConnectionTableUpstreamDatabasesQuery_virtualConnectionTables_upstreamDatabasesConnection_nodes;
type GridRowData = DatabasesDisplayGridRowData;

type QueryVariablesWithFilter = VirtualConnectionTableUpstreamDatabasesFilteredByColumnQueryVariables;
type QueryResultsWithFilter = VirtualConnectionTableUpstreamDatabasesFilteredByColumnQuery;
type QueryResultListNodeWithFilter =
  VirtualConnectionTableUpstreamDatabasesFilteredByColumnQuery_virtualConnectionTables_columnsConnection_nodes_upstreamDatabasesConnection_nodes;

function selectConnectionListResult(resultData: QueryResults): ConnectionListResult<QueryResultListNode> | null {
  return resultData && resultData.virtualConnectionTables && resultData.virtualConnectionTables[0] &&
    resultData.virtualConnectionTables[0].upstreamDatabasesConnection;
}

function selectConnectionListResultForFilteredQuery(
  resultData: QueryResultsWithFilter
): ConnectionListResult<QueryResultListNodeWithFilter> | null {
  const virtualConnection = resultData && resultData.virtualConnectionTables && resultData.virtualConnectionTables[0];
  const virtualConnectionTableColumns = virtualConnection && virtualConnection.columnsConnection && virtualConnection.columnsConnection.nodes;
  const column = virtualConnectionTableColumns && virtualConnectionTableColumns[0];
  return column ? column.upstreamDatabasesConnection : null;
}

function selectMaxCountsResult(resultData: QueryResults): MaxCountsResultData {
  return resultData && resultData.virtualConnectionTables && resultData.virtualConnectionTables[0];
}

function selectMaxCountsResultForFilteredQuery(resultData: QueryResultsWithFilter): MaxCountsResultData | null {
  const virtualConnection = resultData && resultData.virtualConnectionTables && resultData.virtualConnectionTables[0];
  const virtualConnectionTableColumns = virtualConnection && virtualConnection.columnsConnection && virtualConnection.columnsConnection.nodes;
  const column = virtualConnectionTableColumns && virtualConnectionTableColumns[0];
  return column;
}

function getRowDataForDatabase(
  table: DatabasesDisplayGridRowDataFragment,
  resultData: QueryResults,
): DatabasesDisplayGridRowData {
  return getRowDataWithMaxCountsForDatabase(table, selectMaxCountsResult(resultData));
}

function getRowDataForDatabaseForFilteredQuery(
  table: DatabasesDisplayGridRowDataFragment,
  resultData: QueryResultsWithFilter,
): DatabasesDisplayGridRowData {
  return getRowDataWithMaxCountsForDatabase(table, selectMaxCountsResultForFilteredQuery(resultData));
}

export type VirtualConnectionTableUpstreamDatabasesQueryGridProps = GridSortProps<DatabasesValidSortFields> & {
  readonly vizportalUrlId: string;
  readonly filteredColumnId: string | null;
  readonly rowCount: number | null;
};

export function VirtualConnectionTableUpstreamDatabasesQueryGrid(props: VirtualConnectionTableUpstreamDatabasesQueryGridProps): JSX.Element {
  const { publishedConnectionsLineageEnabled } = React.useContext(FeatureFlagsContext);

  const emptyGridMessage = Messages.EmptyUpstreamDatabasesLineageGridMessage();
  const emptyFilteredGridMessage = Messages.EmptyUpstreamFilteredDatabasesLineageGridMessage();

  return props.filteredColumnId ? (
    // Query with Selected Database as Filter
    <PaginatedQueryWrapper<QueryVariablesWithFilter, QueryResultsWithFilter, QueryResultListNode, GridRowData>
      queryGql={VirtualConnectionTableUpstreamDatabasesFilteredByColumnQueryGql}
      partialQueryVariables={{
        vizportalUrlId: props.vizportalUrlId,
        selectedColumnId: props.filteredColumnId,
        orderBy: props.orderBy as DatabaseSortOrder,
        publishedConnectionsLineageEnabled
      }}
      selectConnectionListResult={selectConnectionListResultForFilteredQuery}
      mapResultNode={getRowDataForDatabaseForFilteredQuery}
    >
      {({ rowStore }) =>
        <DatabasesDisplayGrid
          rowStore={rowStore}
          emptyGridMessage={emptyFilteredGridMessage}
          rowCount={props.rowCount}
          orderBy={props.orderBy}
          updateSortOrder={props.updateSortOrder}
        />
      }
    </PaginatedQueryWrapper>
  ) : (
    // Query without Selection
    <PaginatedQueryWrapper<QueryVariables, QueryResults, QueryResultListNode, GridRowData>
      queryGql={VirtualConnectionTableUpstreamDatabasesQueryGql}
      partialQueryVariables={{
        vizportalUrlId: props.vizportalUrlId,
        orderBy: props.orderBy as DatabaseSortOrder,
        publishedConnectionsLineageEnabled
      }}
      selectConnectionListResult={selectConnectionListResult}
      mapResultNode={getRowDataForDatabase}
    >
      {({ rowStore }) =>
        <DatabasesDisplayGrid
          rowStore={rowStore}
          emptyGridMessage={emptyGridMessage}
          rowCount={props.rowCount}
          orderBy={props.orderBy}
          updateSortOrder={props.updateSortOrder}
        />}
    </PaginatedQueryWrapper>
  );
}
